export interface StoreThemeTokens {
  id: string;
  name: string;
  description: string;
  swatch: string[];
  bgMain: string;
  bgSecondary: string;
  bgHeader: string;
  bgHeaderBorder: string;
  bgFooter: string;
  bgFooterText: string;
  textPrimary: string;
  textSecondary: string;
  accent: string;
  accentHover: string;
  accentLight: string;
  accentDark: string;
  cardBorder: string;
  cardBg: string;
  heroGradient: string;
  fontSerif: string;
  fontSans: string;
  fontScript: string;
  badgeBg: string;
  badgeText: string;
  trustBadgeBg: string;
  brandNoteGradient: string;
  brandNoteBorder: string;
  isDark?: boolean;
}

export const STORE_THEMES: Record<string, StoreThemeTokens> = {
  luxury_warm: {
    id: "luxury_warm",
    name: "Rudrix Luxury Warm",
    description: "Warm cream, blush tones, copper highlights, and elegant serif typography",
    swatch: ["#FAF5F0", "#C5896E", "#EBDCD0", "#1C1917"],
    bgMain: "#FAF5F0",
    bgSecondary: "#F7EFE9",
    bgHeader: "rgba(250, 245, 240, 0.95)",
    bgHeaderBorder: "rgba(235, 220, 208, 0.8)",
    bgFooter: "#1C1917",
    bgFooterText: "#EBDCD0",
    textPrimary: "#1C1917",
    textSecondary: "#78716C",
    accent: "#C5896E",
    accentHover: "#B2684C",
    accentLight: "#F3E5DD",
    accentDark: "#8C4B30",
    cardBorder: "#EBDCD0",
    cardBg: "#FFFFFF",
    heroGradient: "linear-gradient(135deg, #FAF3ED 0%, #F4E6DC 50%, #EBDCD0 100%)",
    fontSerif: "'Cormorant Garamond', Georgia, serif",
    fontSans: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
    fontScript: "'Caveat', cursive",
    badgeBg: "#C5896E",
    badgeText: "#FFFFFF",
    trustBadgeBg: "#FFFFFF",
    brandNoteGradient: "linear-gradient(135deg, #F7EBE1 0%, #F4DFD2 50%, #EED8CA 100%)",
    brandNoteBorder: "#EBDCD0",
    isDark: false,
  },
  modern_minimal: {
    id: "modern_minimal",
    name: "Modern Clean",
    description: "Crisp whites, cool slate grays, vibrant indigo accents, and clean geometric sans",
    swatch: ["#F8FAFC", "#2563EB", "#E2E8F0", "#0F172A"],
    bgMain: "#F8FAFC",
    bgSecondary: "#F1F5F9",
    bgHeader: "rgba(255, 255, 255, 0.95)",
    bgHeaderBorder: "#E2E8F0",
    bgFooter: "#0F172A",
    bgFooterText: "#94A3B8",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    accent: "#2563EB",
    accentHover: "#1D4ED8",
    accentLight: "#EFF6FF",
    accentDark: "#1E40AF",
    cardBorder: "#E2E8F0",
    cardBg: "#FFFFFF",
    heroGradient: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 50%, #BFDBFE 100%)",
    fontSerif: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontSans: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontScript: "'Caveat', cursive",
    badgeBg: "#2563EB",
    badgeText: "#FFFFFF",
    trustBadgeBg: "#FFFFFF",
    brandNoteGradient: "linear-gradient(135deg, #EFF6FF 0%, #E0E7FF 100%)",
    brandNoteBorder: "#BFDBFE",
    isDark: false,
  },
  midnight_luxe: {
    id: "midnight_luxe",
    name: "Midnight Obsidian",
    description: "Deep obsidian black, dark carbon cards, champagne gold accents, and luxury serif",
    swatch: ["#121212", "#D4AF37", "#2A2A2A", "#FFFFFF"],
    bgMain: "#121212",
    bgSecondary: "#1A1A1A",
    bgHeader: "rgba(18, 18, 18, 0.95)",
    bgHeaderBorder: "#2A2A2A",
    bgFooter: "#0A0A0A",
    bgFooterText: "#A3A3A3",
    textPrimary: "#F5F5F5",
    textSecondary: "#A3A3A3",
    accent: "#D4AF37",
    accentHover: "#C59E2E",
    accentLight: "#2A2415",
    accentDark: "#9E7C17",
    cardBorder: "#2A2A2A",
    cardBg: "#1C1C1C",
    heroGradient: "linear-gradient(135deg, #1C1914 0%, #292217 50%, #1A1713 100%)",
    fontSerif: "'Cormorant Garamond', Georgia, serif",
    fontSans: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontScript: "'Caveat', cursive",
    badgeBg: "#D4AF37",
    badgeText: "#121212",
    trustBadgeBg: "#1E1E1E",
    brandNoteGradient: "linear-gradient(135deg, #1C1914 0%, #241F16 100%)",
    brandNoteBorder: "#3D3322",
    isDark: true,
  },
  fresh_organic: {
    id: "fresh_organic",
    name: "Fresh Botanical",
    description: "Soft sage, refreshing herbal greens, natural earth tones, and healthy wellness vibe",
    swatch: ["#F6FAF6", "#2E7D32", "#D1E7D3", "#143118"],
    bgMain: "#F6FAF6",
    bgSecondary: "#EAF4EB",
    bgHeader: "rgba(246, 250, 246, 0.95)",
    bgHeaderBorder: "#D1E7D3",
    bgFooter: "#143118",
    bgFooterText: "#D1E7D3",
    textPrimary: "#19351C",
    textSecondary: "#527855",
    accent: "#2E7D32",
    accentHover: "#1B5E20",
    accentLight: "#E8F5E9",
    accentDark: "#134617",
    cardBorder: "#D1E7D3",
    cardBg: "#FFFFFF",
    heroGradient: "linear-gradient(135deg, #F1F8F2 0%, #E2F0E4 50%, #D4EAD6 100%)",
    fontSerif: "'Cormorant Garamond', Georgia, serif",
    fontSans: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontScript: "'Caveat', cursive",
    badgeBg: "#2E7D32",
    badgeText: "#FFFFFF",
    trustBadgeBg: "#FFFFFF",
    brandNoteGradient: "linear-gradient(135deg, #EBF5EC 0%, #DEF0DF 100%)",
    brandNoteBorder: "#C8E6C9",
    isDark: false,
  },
};

export const DEFAULT_THEME_ID = "luxury_warm";

export const getStoreTheme = (themeData?: any): StoreThemeTokens => {
  const themeId = themeData?.theme_id || DEFAULT_THEME_ID;
  const baseTheme = STORE_THEMES[themeId] || STORE_THEMES[DEFAULT_THEME_ID];

  // If a custom accent color is provided, apply it dynamically
  if (themeData?.accent_color && themeData.accent_color !== baseTheme.accent) {
    return {
      ...baseTheme,
      accent: themeData.accent_color,
      badgeBg: themeData.accent_color,
    };
  }

  return baseTheme;
};
